import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import api from '../api';

function Section({ title, children }) {
  return (
    <div style={{ background: 'white', borderRadius: 14, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #e5e7eb', marginBottom: 24 }}>
      <h3 style={{ margin: '0 0 20px', fontSize: '1.05rem', fontWeight: 700, color: '#1f2937', borderBottom: '2px solid #e5e7eb', paddingBottom: 12 }}>{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, value, onChange, type = 'text', rows }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: '0.82rem', color: '#6b7280', marginBottom: 4 }}>{label}</label>
      {rows ? (
        <textarea value={value} onChange={e => onChange(e.target.value)} rows={rows}
          style={{ width: '100%', borderRadius: 8, border: '1px solid #d1d5db', padding: '10px 12px', fontSize: '0.95rem', fontFamily: 'inherit', resize: 'vertical', boxSizing: 'border-box' }} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)}
          style={{ width: '100%', borderRadius: 8, border: '1px solid #d1d5db', padding: '10px 12px', fontSize: '0.95rem', boxSizing: 'border-box' }} />
      )}
    </div>
  );
}

// ── Photo Upload Drop Zone + Position Controls ───────────────────────────────
function PhotoUpload({ photoUrl, position, onUploaded, onPositionChange }) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = React.useRef();

  // position is "X% Y%" e.g. "50% 20%"
  function parsePos(pos) {
    const [x, y] = (pos || '50% 50%').replace(/%/g, '').split(' ').map(Number);
    return { x: isNaN(x) ? 50 : x, y: isNaN(y) ? 50 : y };
  }
  function move(dx, dy) {
    const { x, y } = parsePos(position);
    const nx = Math.max(0, Math.min(100, x + dx));
    const ny = Math.max(0, Math.min(100, y + dy));
    onPositionChange(`${nx}% ${ny}%`);
  }

  async function upload(file) {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('photo', file);
    try {
      const r = await api.post('/portal/about/upload-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onUploaded(`https://mabat.communityltd.co.il${r.data.url}`);
    } catch { alert('שגיאה בהעלאה'); }
    setUploading(false);
  }

  const btnStyle = { width: 32, height: 32, border: '1px solid #d1d5db', borderRadius: 6, background: 'white', cursor: 'pointer', fontSize: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, flexShrink: 0 }}>
      {/* Preview circle */}
      <div style={{ fontSize: '0.72rem', color: '#6b7280', marginBottom: 2, fontWeight: 600 }}>תצוגה מקדימה</div>
      <div style={{ width: 120, height: 120, borderRadius: '50%', overflow: 'hidden', border: '3px solid #1a4a8a', background: '#f9fafb', position: 'relative', flexShrink: 0 }}>
        {photoUrl
          ? <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: position || '50% 50%' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5rem', color: '#9ca3af' }}>👤</div>}
      </div>

      {/* Arrow controls — only show when photo exists */}
      {photoUrl && (
        <div style={{ display: 'grid', gridTemplateColumns: '32px 32px 32px', gridTemplateRows: '32px 32px 32px', gap: 3 }}>
          <div />
          <button style={btnStyle} onClick={() => move(0, -10)} title="למעלה">↑</button>
          <div />
          <button style={btnStyle} onClick={() => move(-10, 0)} title="שמאלה">←</button>
          <div style={{ ...btnStyle, background: '#f9fafb', cursor: 'default', fontSize: '0.6rem', color: '#9ca3af' }}>🎯</div>
          <button style={btnStyle} onClick={() => move(10, 0)} title="ימינה">→</button>
          <div />
          <button style={btnStyle} onClick={() => move(0, 10)} title="למטה">↓</button>
          <div />
        </div>
      )}

      {/* Upload zone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={e => { e.preventDefault(); setDragging(false); upload(e.dataTransfer.files[0]); }}
        style={{
          width: 120, borderRadius: 10, cursor: 'pointer', padding: '8px 0',
          border: `2px ${dragging ? 'solid #1a4a8a' : 'dashed #9ca3af'}`,
          background: dragging ? '#eff6ff' : '#f9fafb',
          textAlign: 'center', transition: 'border-color 0.15s',
        }}>
        {uploading
          ? <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>⏳ מעלה...</span>
          : <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>📷 לחץ או גרור תמונה</span>}
      </div>
      <input ref={inputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => upload(e.target.files[0])} />
    </div>
  );
}

// ── Founder Section ─────────────────────────────────────────────────────────
function FounderSection({ projectName }) {
  const [data, setData] = useState({ founder_name: '', title: '', years: '', description: '', photo_url: '', photo_position: '50% 20%' });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get(`/portal/about/founder/${encodeURIComponent(projectName)}`)
      .then(r => setData({
        founder_name: r.data.founder_name || '',
        title: r.data.title || '',
        years: r.data.years || '',
        description: r.data.description || '',
        photo_url: r.data.photo_url || '',
        photo_position: r.data.photo_position || '50% 20%',
      }))
      .catch(() => {});
  }, [projectName]);

  async function save() {
    setSaving(true);
    await api.post(`/portal/about/founder/${encodeURIComponent(projectName)}`, data);
    setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2000);
  }

  function set(k) { return v => setData(d => ({ ...d, [k]: v })); }

  return (
    <Section title="👑 מייסד / תיאור החברה">
      <div style={{ display: 'flex', gap: 28, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <PhotoUpload
          photoUrl={data.photo_url}
          position={data.photo_position}
          onUploaded={url => setData(d => ({ ...d, photo_url: url }))}
          onPositionChange={pos => setData(d => ({ ...d, photo_position: pos }))}
        />
        <div style={{ flex: 1, minWidth: 260 }}>
          <Field label="שם המייסד" value={data.founder_name} onChange={set('founder_name')} />
          <Field label="תפקיד / כותרת" value={data.title} onChange={set('title')} />
          <Field label="שנים (למשל 1931 – 2010)" value={data.years} onChange={set('years')} />
          <Field label="תיאור החברה / המייסד..." value={data.description} onChange={set('description')} rows={4} />
        </div>
      </div>
      <button onClick={save} disabled={saving}
        style={{ marginTop: 16, background: saving ? '#9ca3af' : '#1a4a8a', color: 'white', border: 'none', borderRadius: 8, padding: '10px 28px', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}>
        {saved ? '✓ נשמר' : saving ? 'שומר...' : '💾 שמור'}
      </button>
    </Section>
  );
}

// ── Projects List Section ─────────────────────────────────────────────────────
function ProjectsSection({ projectName }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null); // null = hidden, {} = new, {id,...} = edit
  const [draft, setDraft] = useState({ name: '', city: '', url: '' });

  useEffect(() => {
    api.get(`/portal/about/projects/${encodeURIComponent(projectName)}`).then(r => setList(r.data)).catch(() => {});
  }, [projectName]);

  function openNew() { setDraft({ name: '', city: '', url: '' }); setForm({}); }
  function openEdit(item) { setDraft({ name: item.name || '', city: item.city || '', url: item.url || '' }); setForm(item); }

  async function save() {
    if (form.id) {
      await api.patch(`/portal/about/projects/${form.id}`, draft);
      setList(l => l.map(x => x.id === form.id ? { ...x, ...draft } : x));
    } else {
      const r = await api.post(`/portal/about/projects/${encodeURIComponent(projectName)}`, draft);
      setList(l => [...l, { id: r.data.id, ...draft }]);
    }
    setForm(null);
  }

  async function del(id) {
    if (!window.confirm('למחוק?')) return;
    await api.delete(`/portal/about/projects/${id}`);
    setList(l => l.filter(x => x.id !== id));
  }

  return (
    <Section title="📌 רשימת פרויקטים">
      <button onClick={openNew}
        style={{ background: '#1a4a8a', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 700, cursor: 'pointer', marginBottom: 16 }}>
        + הוסף
      </button>
      {form !== null && (
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #e5e7eb' }}>
          <Field label="שם הפרויקט" value={draft.name} onChange={v => setDraft(d => ({ ...d, name: v }))} />
          <Field label="עיר" value={draft.city} onChange={v => setDraft(d => ({ ...d, city: v }))} />
          <Field label="קישור (URL)" value={draft.url} onChange={v => setDraft(d => ({ ...d, url: v }))} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} style={{ background: '#1a4a8a', color: 'white', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, cursor: 'pointer' }}>הוסף</button>
            <button onClick={() => setForm(null)} style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>ביטול</button>
          </div>
        </div>
      )}
      {list.map(item => (
        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#1f2937' }}>{item.name}</div>
            {item.city && <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>📍 {item.city}</div>}
            {item.url && <a href={item.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', color: '#1a4a8a' }}>{item.url}</a>}
          </div>
          <button onClick={() => openEdit(item)} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.85rem' }}>✏️</button>
          <button onClick={() => del(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.9rem' }}>מחק</button>
        </div>
      ))}
      {list.length === 0 && <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין פרויקטים עדיין</div>}
    </Section>
  );
}

// ── Press Section ─────────────────────────────────────────────────────────────
function PressSection({ projectName }) {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [draft, setDraft] = useState({ title: '', source: '', date_str: '', url: '' });

  useEffect(() => {
    api.get(`/portal/about/press/${encodeURIComponent(projectName)}`).then(r => setList(r.data)).catch(() => {});
  }, [projectName]);

  function openNew() { setDraft({ title: '', source: '', date_str: '', url: '' }); setForm({}); }
  function openEdit(item) { setDraft({ title: item.title || '', source: item.source || '', date_str: item.date_str || '', url: item.url || '' }); setForm(item); }

  async function save() {
    if (form.id) {
      await api.patch(`/portal/about/press/${form.id}`, draft);
      setList(l => l.map(x => x.id === form.id ? { ...x, ...draft } : x));
    } else {
      const r = await api.post(`/portal/about/press/${encodeURIComponent(projectName)}`, draft);
      setList(l => [{ id: r.data.id, ...draft }, ...l]);
    }
    setForm(null);
  }

  async function del(id) {
    if (!window.confirm('למחוק?')) return;
    await api.delete(`/portal/about/press/${id}`);
    setList(l => l.filter(x => x.id !== id));
  }

  return (
    <Section title="📰 מן העיתונות">
      <button onClick={openNew}
        style={{ background: '#1a4a8a', color: 'white', border: 'none', borderRadius: 8, padding: '8px 18px', fontWeight: 700, cursor: 'pointer', marginBottom: 16 }}>
        + הוסף
      </button>
      {form !== null && (
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, marginBottom: 16, border: '1px solid #e5e7eb' }}>
          <Field label="כותרת *" value={draft.title} onChange={v => setDraft(d => ({ ...d, title: v }))} />
          <Field label="מקור (גלובס, כלכליסט...)" value={draft.source} onChange={v => setDraft(d => ({ ...d, source: v }))} />
          <Field label="תאריך (ינואר 2024)" value={draft.date_str} onChange={v => setDraft(d => ({ ...d, date_str: v }))} />
          <Field label="קישור לכתבה (https://...)" value={draft.url} onChange={v => setDraft(d => ({ ...d, url: v }))} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={save} style={{ background: '#1a4a8a', color: 'white', border: 'none', borderRadius: 8, padding: '8px 20px', fontWeight: 700, cursor: 'pointer' }}>הוסף</button>
            <button onClick={() => setForm(null)} style={{ background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>ביטול</button>
          </div>
        </div>
      )}
      {list.map(item => (
        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, color: '#1f2937' }}>{item.title}</div>
            <div style={{ fontSize: '0.82rem', color: '#6b7280' }}>
              {item.source && <span>{item.source}</span>}
              {item.source && item.date_str && <span> · </span>}
              {item.date_str && <span>{item.date_str}</span>}
            </div>
            {item.url && <a href={item.url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', color: '#1a4a8a' }}>{item.url}</a>}
          </div>
          <button onClick={() => openEdit(item)} style={{ background: 'none', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: '0.85rem' }}>✏️</button>
          <button onClick={() => del(item.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '0.9rem' }}>מחק</button>
        </div>
      ))}
      {list.length === 0 && <div style={{ color: '#9ca3af', fontSize: '0.9rem' }}>אין כתבות עדיין</div>}
    </Section>
  );
}

export default function About() {
  const { projectName } = useParams();
  const navigate = useNavigate();
  const name = decodeURIComponent(projectName);

  return (
    <div>
      <Navbar title={name} />
      <div className="container" style={{ maxWidth: 800, margin: '0 auto', padding: '0 16px' }}>
        <button onClick={() => navigate(`/project-hub/${projectName}`)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.9rem', margin: '16px 0', padding: 0 }}>
          ← חזרה
        </button>
        <h2 className="page-title" style={{ marginBottom: 24 }}>אודות הפרויקט</h2>
        <FounderSection projectName={name} />
        <ProjectsSection projectName={name} />
        <PressSection projectName={name} />
      </div>
    </div>
  );
}
